import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, TrendingUp, Users, Target, DollarSign, Zap, Award, Flag, Briefcase } from 'lucide-react';
import { benefits } from '../data/mock';

const iconMap = { TrendingUp, Users, Target, DollarSign, Zap, Award };

const SERVICES = [
  { to: '/services/revscale', icon: TrendingUp, title: 'RevScale Fractional CSO/CRO', desc: 'CRO, CSO & GTM Strategy' },
  { to: '/services/leadership-hiring', icon: Users, title: 'Leadership Hiring', desc: 'Executive search & placement' },
  { to: '/services/product-roadmapping', icon: Briefcase, title: 'Product Roadmapping', desc: 'Marketing, relevance & roadmap' },
  { to: '/services/corporate-golf', icon: Flag, title: 'Golf for Corporate Success', desc: 'Executive networking on the course' },
];

export default function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen hero-gradient hero-texture flex items-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-gradient/20 blur-3xl" />
        </div>
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-[15%] w-3 h-3 rounded-full bg-accent opacity-60" style={{ animation: '4s ease-in-out 0s infinite floatSubtle' }} />
          <div className="absolute top-1/3 right-[20%] w-2 h-2 rounded-full bg-accent opacity-40" style={{ animation: '5s ease-in-out 1s infinite floatSubtle' }} />
          <div className="absolute bottom-1/3 left-[25%] w-4 h-4 rounded-full bg-accent opacity-30" style={{ animation: '6s ease-in-out 0.5s infinite floatSubtle' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
          {/* Services Strip - Top */}
          <div className="mb-12" data-testid="services-strip">
            <h3 className="text-base lg:text-lg font-bold text-accent tracking-wide mb-6 text-center">We Offer Exclusive Corporate Consulting Services For The Teams Of Tomorrow! Click Below To Explore How We Work</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {SERVICES.map((svc) => {
                const Icon = svc.icon;
                return (
                  <Link key={svc.to} to={svc.to} className="flex items-center gap-3 p-4 lg:p-5 rounded-xl bg-white/15 border border-white/20 hover:bg-white/25 hover:border-white/35 transition-all group backdrop-blur-md">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0 group-hover:bg-white/25 transition-colors">
                      <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm lg:text-base font-bold text-white group-hover:text-accent transition-colors truncate">{svc.title}</p>
                      <p className="text-xs lg:text-sm text-white/60 truncate">{svc.desc}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Hero Content */}
          <div className="max-w-3xl" data-testid="hero-content">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
                <Sparkles className="w-4 h-4 text-accent" />
                <span className="text-sm font-medium text-white/90">Fractional Leadership for Tech Startups</span>
              </div>

              <div className="mb-5">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight">
                  Scale Your Revenue Without Breaking The Bank
                </h1>
                <span className="block text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-accent leading-tight mt-2">
                  <span className="relative inline-block">
                    in weeks.
                    <span className="absolute bottom-0 left-0 w-full h-1 bg-accent/50 rounded" />
                  </span>
                </span>
              </div>

              <p className="text-lg sm:text-xl text-white/75 max-w-lg mb-8 leading-relaxed">
                Get seasoned CRO & CSO expertise at a fraction of the cost. Build your sales engine with proven executives who've scaled companies from $0 to $100M+.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-8 py-4 hover:bg-accent/90 hover-lift transition-all group"
                >
                  Schedule Consultation
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 border border-white/25 text-white font-semibold text-lg px-8 py-4 hover:bg-white/20 transition-all"
                >
                  Explore Services
                </Link>
              </div>
            </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-background border-y border-border" data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-4">
            {[
              { value: '60-70%', label: 'Cost Savings vs Full-Time' },
              { value: '1-2 Weeks', label: 'Time to Get Started' },
              { value: '20+ Years', label: 'Average Experience' },
            ].map((stat, i) => (
              <div key={i} className="relative text-center lg:px-6">
                <div className="text-4xl sm:text-5xl font-bold text-primary mb-2 stat-counter">{stat.value}</div>
                <p className="text-muted-foreground text-sm sm:text-base">{stat.label}</p>
                {i < 2 && <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-accent" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 bg-primary/5" data-testid="value-props-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {[
              { accent: '15+ years', title: ' of combined industry experience', desc: 'Programs shaped by professionals with deep, hands-on experience across sales, GTM, and revenue operations.' },
              { accent: 'Practical', title: ' and implementable leadership', desc: 'Everything we do is designed to create immediate impact at work, not just slide decks and frameworks.' },
              { accent: 'Trusted', title: ' by startup teams', desc: 'Proven approaches that have supported revenue growth across 100+ organizations from pre-seed to Series B.' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="mb-4">
                  <span className="text-2xl lg:text-3xl font-bold text-accent">{item.accent}</span>
                  <span className="text-2xl lg:text-3xl font-bold text-foreground">{item.title}</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                <div className="w-12 h-0.5 bg-accent/30 mx-auto mt-6" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Real Roles Section */}
      <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground text-center mb-16">
            Built for real growth, not theory
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {benefits.slice(0, 3).map((benefit, i) => {
              const Icon = iconMap[benefit.icon] || DollarSign;
              return (
                <div key={benefit.id} className="card-depth rounded-2xl p-8 lg:p-10" style={{ transform: i === 1 ? 'translateY(-8px)' : 'none' }}>
                  <div className="w-12 h-1 bg-accent rounded-full mb-6" />
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <p className="text-xl font-bold text-foreground mb-3">{benefit.title}</p>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="services-preview">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">Our Leadership Services</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Each service is built around how growth actually happens in your domain.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { to: '/services/revscale', icon: TrendingUp, title: 'RevScale Fractional CSO/CRO', desc: 'CRO, CSO & GTM Strategy — strategic revenue leadership to scale your business.' },
              { to: '/services/leadership-hiring', icon: Users, title: 'Leadership Hiring', desc: 'Find and place the right revenue leaders for your stage with our executive search.' },
              { to: '/services/product-roadmapping', icon: Target, title: 'Product Roadmapping', desc: 'Align product strategy with market needs — marketing, relevance & CTO/CPO alignment.' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Link to={item.to} key={item.to} className="group card-depth rounded-2xl p-6 cursor-pointer">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-lg font-bold text-foreground group-hover:text-accent transition-colors duration-300">{item.title}</p>
                  <p className="text-muted-foreground text-sm mt-1 mb-4">{item.desc}</p>
                  <div className="flex items-center text-sm font-medium text-primary group-hover:text-accent transition-colors duration-300">
                    <span>Learn more</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 cta-gradient animate-gradient" />
        <div className="absolute inset-0 hero-texture opacity-50" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8">
            Ready to scale your revenue?
          </h2>
          <Link
            to="/contact"
            data-testid="cta-contact-btn"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-10 py-4 hover:bg-accent/90 hover-lift group"
          >
            Contact us
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
